import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "../i18n";
import { listDestaques } from "../lib/highlightsService";
import { listPartners } from "../lib/partnersService";
import "../styles/Destaques.css";

/**
 * Página de Destaques — conteúdo próprio da Mixórdia (ex-"Vitrine de
 * parceiros"). Rota: /showcase — ver App.js.
 *
 * Dois modos navegáveis:
 *
 *   "tune" — a TV de tubo vira o player. Cada gravação é um canal, com
 *            chiado na troca. A grade embaixo é só o seletor.
 *   "grid" — sem TV. Grade de programação cronológica; a linha abre e o
 *            player nasce dentro dela.
 *
 * Nos dois casos o vídeo toca DENTRO do site. O player só é criado no
 * clique (padrão facade): 21 iframes carregados de uma vez derrubariam a
 * página, e nada de terceiro toca cookie antes de um gesto do usuário.
 */


const YT_ID = /(?:youtu\.be\/|[?&]v=)([\w-]{11})/;

const youtubeId = (url) => (url && url.match(YT_ID)?.[1]) || null;

const embedUrl = (item) => {
  const id = youtubeId(item.external_url);
  if (id) {
    // nocookie + autoplay: o usuário já clicou, então tocar é o esperado
    return `https://www.youtube-nocookie.com/embed/${id}?autoplay=1&rel=0&modestbranding=1`;
  }
  return `https://w.soundcloud.com/player/?url=${encodeURIComponent(
    item.external_url
  )}&color=%23E21A37&auto_play=true&hide_related=true&show_comments=false&visual=true`;
};

const channelLabel = (i) => String(i + 1).padStart(2, "0");

/**
 * Avisa a rádio de fundo (SoundPlayer) que ela precisa sair de cena.
 * Dois áudios ao mesmo tempo é sempre erro — e sem isso o próprio clique
 * no play poderia religar a rádio, por causa dos listeners de gesto que
 * o SoundPlayer registra depois de um reload.
 */
const silenciarRadio = () => window.dispatchEvent(new Event("mxd:pause-radio"));


/** Quantas gravações aparecem antes do "ver mais". */
const GRAVACOES_VISIVEIS = 8;


/**
 * Setas de uma faixa horizontal. Só existem quando a faixa realmente
 * transborda — em tela larga com poucos itens elas somem, porque seta
 * que não leva a lugar nenhum é ruído.
 */
const useFaixa = (qtdItens) => {
  const ref = useRef(null);
  const [pode, setPode] = useState({ esq: false, dir: false });
  // centro vertical da IMAGEM do primeiro card, em px
  const [centro, setCentro] = useState(null);

  const medir = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    setPode({
      esq: el.scrollLeft > 4,
      dir: el.scrollLeft + el.clientWidth < el.scrollWidth - 4,
    });

    // Medido, não estimado: porcentagem da altura do card não sobrevive à
    // troca de breakpoint, porque a proporção entre imagem e texto muda.
    const slot = el.querySelector(".ritual-slot, .merch-slot");
    if (slot) setCentro(slot.offsetTop + slot.offsetHeight / 2);
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    medir();
    el.addEventListener("scroll", medir, { passive: true });
    const ro = new ResizeObserver(medir);
    ro.observe(el);
    return () => {
      el.removeEventListener("scroll", medir);
      ro.disconnect();
    };
  }, [medir, qtdItens]);

  // rola ~80% da largura visível: sobra uma peça de referência na tela
  const mover = (sentido) => {
    const el = ref.current;
    if (!el) return;
    el.scrollBy({ left: sentido * Math.round(el.clientWidth * 0.8), behavior: "smooth" });
  };

  return { ref, pode, mover, centro };
};


/**
 * Setas sobre as bordas da faixa, não no cabeçalho: elas precisam ficar
 * em cima do card cortado, que é o que sinaliza que há mais conteúdo.
 * No cabeçalho, o corte e o controle ficavam longe um do outro e o
 * usuário demorava a ligar uma coisa na outra.
 *
 * Cada seta só existe quando há para onde ir — nada de botão morto
 * ocupando a borda.
 */
const Setas = ({ pode, mover, rotulo, centro }) => {
  const { t } = useTranslation();
  const estilo = centro == null ? undefined : { top: `${Math.round(centro)}px` };
  return (
    <>
      {pode.esq && (
        <button
          className="faixa-seta faixa-seta--esq"
          style={estilo}
          onClick={() => mover(-1)}
          aria-label={t("destaques.arrowPrevAria", { rotulo })}
        >
          ‹
        </button>
      )}
      {pode.dir && (
        <button
          className="faixa-seta faixa-seta--dir"
          style={estilo}
          onClick={() => mover(1)}
          aria-label={t("destaques.arrowNextAria", { rotulo })}
        >
          ›
        </button>
      )}
    </>
  );
};

const shortDate = (dateStr) => {
  if (!dateStr) return null;
  const [, m, d] = dateStr.split("-");
  return `${d}.${m}`;
};


const Destaques = ({ setShowForm }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [mode, setMode] = useState("tune");
  const [data, setData] = useState({ rituais: [], merch: [], gravacoes: [] });
  const [parceiros, setParceiros] = useState([]);
  // "loading" | "error" | "ready" — o carregamento próprio decide o que a
  // tela inteira mostra. Parceiro não entra aqui: falha dele é silenciosa.
  const [status, setStatus] = useState("loading");
  const [retrying, setRetrying] = useState(false);
  const [retryToken, setRetryToken] = useState(0);
  // Guard síncrono: clique duplo rápido no retry chega antes do re-render
  // que desabilitaria o botão, então o state `retrying` sozinho não segura.
  const retryingRef = useRef(false);

  // canal atualmente sintonizado (modo tune)
  const [current, setCurrent] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [tuning, setTuning] = useState(false);

  // linha aberta (modo grid)
  const [openRow, setOpenRow] = useState(null);
  // guia de canais começa cortado; o acervo cresce sem teto
  const [mostrarTudo, setMostrarTudo] = useState(false);

  const tuneTimer = useRef(null);
  const tvRef = useRef(null);

  /**
   * Duas queries, dois efeitos independentes — de propósito, não por
   * enfeite. Destaques tem uma máquina de estados inteira (loading / erro /
   * pronto) com retry; parceiro não tem nenhuma UI de erro (é "algo não tão
   * relevante", pedido do Lucas) e nunca é re-tentado. Um Promise.allSettled
   * único ainda exigiria rastrear os dois status separadamente pra não
   * re-buscar parceiro a cada retry de destaques — separar os efeitos evita
   * esse acoplamento artificial.
   */
  useEffect(() => {
    let cancelled = false;
    // retryToken > 0 é retry: fura o cache. A busca inicial não.
    listDestaques({ force: retryToken > 0 })
      .then((d) => {
        if (cancelled) return;
        setData(d);
        setStatus("ready");
      })
      .catch((e) => {
        if (cancelled) return;
        console.error("[destaques] fetch", e);
        setStatus("error");
      })
      .finally(() => {
        if (cancelled) return;
        retryingRef.current = false;
        setRetrying(false);
      });
    return () => { cancelled = true; };
  }, [retryToken]);

  useEffect(() => {
    let cancelled = false;
    listPartners()
      .then((p) => { if (!cancelled) setParceiros(p); })
      .catch((e) => console.error("[destaques] partners fetch", e));
    return () => { cancelled = true; };
  }, []);

  const retry = useCallback(() => {
    if (retryingRef.current) return;
    retryingRef.current = true;
    setRetrying(true);
    setRetryToken((t) => t + 1);
  }, []);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  useEffect(() => () => clearTimeout(tuneTimer.current), []);

  const gravacoes = data.gravacoes;

  /** Troca de canal: chiado curto antes do conteúdo novo aparecer. */
  const tuneTo = useCallback((item) => {
    if (current?.id === item.id) return;
    clearTimeout(tuneTimer.current);
    setTuning(true);
    setPlaying(false);

    // No mobile a TV não é sticky e o guia fica abaixo dela: sem isso o
    // usuário toca num canal e não vê nada acontecer.
    if (window.innerWidth <= 900) {
      tvRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }

    tuneTimer.current = setTimeout(() => {
      setCurrent(item);
      setTuning(false);
    }, 320);
  }, [current]);

  const close = useCallback(() => navigate("/"), [navigate]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") return close();
      if (mode !== "tune" || gravacoes.length === 0) return;
      if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
      e.preventDefault();
      const idx = gravacoes.findIndex((g) => g.id === current?.id);
      const next = e.key === "ArrowDown"
        ? (idx + 1) % gravacoes.length
        : (idx - 1 + gravacoes.length) % gravacoes.length;
      tuneTo(gravacoes[next]);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [mode, gravacoes, current, tuneTo, close]);

  return (
    <div className="dest-root">
      <header className="dest-bar">
        <div className="dest-brand">
          <span className="dest-dot" aria-hidden="true" />
          {/* Abaixo de 480px só "destaques" fica: o rótulo inteiro ocupava os
              119px que faltavam pro seletor de modo caber sem sobrepor. */}
          <span className="dest-brand-label">
            <span className="dest-brand-marca">mixórdia · </span>{t("destaques.brandLabel")}
          </span>
        </div>

        <div className="dest-modes" role="group" aria-label={t("destaques.modeGroupAria")}>
          <button
            className={mode === "tune" ? "is-on" : ""}
            onClick={() => setMode("tune")}
          >
            {t("destaques.modeTune")}
          </button>
          <button
            className={mode === "grid" ? "is-on" : ""}
            onClick={() => setMode("grid")}
          >
            {t("destaques.modeGrid")}
          </button>
        </div>

        <button className="dest-close" onClick={close} aria-label={t("destaques.closeAria")}>✕</button>
      </header>

      {status === "loading" && <p className="dest-msg">{t("destaques.tuning")}</p>}

      {status === "error" && (
        <div className="dest-msg">
          <p className="tv-standby">
            {t("destaques.noSignal")}
            <em>{t("destaques.errorSub")}</em>
          </p>
          <button className="guide-mais" onClick={retry} disabled={retrying}>
            {retrying ? t("destaques.tuning") : t("destaques.retryButton")}
          </button>
        </div>
      )}

      {status === "ready" && mode === "tune" && (
        <TuneMode
          current={current}
          playing={playing}
          tuning={tuning}
          onPlay={() => { silenciarRadio(); setPlaying(true); }}
          gravacoes={gravacoes}
          rituais={data.rituais}
          merch={data.merch}
          parceiros={parceiros}
          onTune={tuneTo}
          tvRef={tvRef}
          mostrarTudo={mostrarTudo}
          setMostrarTudo={setMostrarTudo}
          setShowForm={setShowForm}
        />
      )}

      {status === "ready" && mode === "grid" && (
        <GridMode
          rituais={data.rituais}
          gravacoes={data.gravacoes}
          merch={data.merch}
          parceiros={parceiros}
          openRow={openRow}
          onToggle={(item) => {
            const abrindo = openRow?.id !== item.id;
            if (abrindo) silenciarRadio();
            setOpenRow(abrindo ? item : null);
          }}
          setShowForm={setShowForm}
        />
      )}
    </div>
  );
};




/* ─────────────────────────── modo SINTONIZAR ─────────────────────────── */

const TuneMode = ({ current, playing, tuning, onPlay, gravacoes, rituais, merch, parceiros, onTune, tvRef, mostrarTudo, setMostrarTudo, setShowForm }) => {
  const { t } = useTranslation();
  return (
  <>
  <Masthead rituais={rituais} gravacoes={gravacoes} />

  {/* Rituais vêm antes do acervo: o que ainda vai acontecer é mais
      urgente que o que já aconteceu. */}
  <RituaisBoard rituais={rituais} />

  <div className="tune">
    <div className="tv" ref={tvRef}>
      <div
        className={`tv-screen ${tuning ? "is-tuning" : ""}`}
        style={{ "--noise": `url(${process.env.PUBLIC_URL}/files/images/crt-noise.png)` }}
      >
        {playing && current ? (
          <iframe
            key={current.id}
            className="tv-frame"
            src={embedUrl(current)}
            title={current.name}
            allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        ) : (
          <button
            className="tv-facade"
            onClick={() => current && onPlay()}
            disabled={!current}
          >
            {current?.image_url && (
              <img src={current.image_url} alt="" />
            )}
            {current ? (
              <span className="tv-play" aria-hidden="true">▶</span>
            ) : (
              <span className="tv-standby">
                {t("destaques.noSignal")}
                <em>{t("destaques.standbySub")}</em>
              </span>
            )}
          </button>
        )}

        <div className="tv-scanlines" aria-hidden="true" />
        <div className="tv-vignette" aria-hidden="true" />

        {current && (
          <div className="tv-osd">
            <span className="tv-osd-ch">
              CH {channelLabel(gravacoes.findIndex((g) => g.id === current.id))}
            </span>
            <span className="tv-osd-name">{current.name}</span>
            <span className="tv-osd-sub">{current.description}</span>
          </div>
        )}
      </div>

      <p className="tv-hint">{t("destaques.tvHint")}</p>
    </div>

    {/* O guia lista só gravações: canal é formato de coisa que se assiste agora.
        Ritual e merch ganham linguagem própria abaixo. */}
    <div className="tune-guide">
      <section className="guide-block">
        <h2>{t("destaques.gravacoesTitle")}</h2>
        <ul>
          {gravacoes.slice(0, mostrarTudo ? undefined : GRAVACOES_VISIVEIS).map((item, i) => (
            <li key={item.id}>
              <button
                className={`guide-row is-playable ${current?.id === item.id ? "is-on" : ""}`}
                onClick={() => onTune(item)}
              >
                <span className="guide-slot">{channelLabel(i)}</span>
                <span className="guide-name">{item.name}</span>
                <span className="guide-sub">{item.description}</span>
                <span className="guide-src">
                  {item.source === "soundcloud" ? t("destaques.sourceAudio") : t("destaques.sourceVideo")}
                </span>
              </button>
            </li>
          ))}
        </ul>

        {gravacoes.length > GRAVACOES_VISIVEIS && (
          <button className="guide-mais" onClick={() => setMostrarTudo(!mostrarTudo)}>
            {mostrarTudo ? t("destaques.seeLess") : t("destaques.seeMore")}
          </button>
        )}
      </section>
    </div>
  </div>

  <MerchEncarte merch={merch} />
  <Parceiros parceiros={parceiros} />
  <Fecho setShowForm={setShowForm} />
  </>
  );
};




/* ─────────────────────────────── PARCEIROS ────────────────────────────
   Quarta linguagem estrutural da página, e a mais quieta de todas: fileira
   de selos. É de propósito — o Lucas pediu que parceria fosse "algo não tão
   relevante", então parceiro não compete com o conteúdo próprio.

   Degrada em três níveis, porque só a Toy Records tem cadastro completo:
   logo → iniciais; com link vira <a>, sem link vira <span> (nada de item
   clicável que não leva a lugar nenhum).
   ───────────────────────────────────────────────────────────────────── */

/** instagram_url às vezes vem como handle ("toyrecords"), às vezes como URL. */
const linkDoParceiro = (p) => {
  if (p.website_url) return p.website_url;
  if (!p.instagram_url) return null;
  return p.instagram_url.startsWith("http")
    ? p.instagram_url
    : `https://instagram.com/${p.instagram_url}`;
};


const Parceiros = ({ parceiros }) => {
  const { t } = useTranslation();
  if (!parceiros?.length) return null;

  return (
    <section className="parceiros">
      <h2 className="parceiros-titulo">{t("destaques.partnersHeading")}</h2>

      <ul className="parceiros-lista">
        {parceiros.map((p) => {
          const href = linkDoParceiro(p);
          const Tag = href ? "a" : "span";
          const props = href
            ? { href, target: "_blank", rel: "noreferrer" }
            : {};

          return (
            <li key={p.id}>
              <Tag className={`parceiro ${href ? "is-link" : ""}`} {...props}>
                <span className="parceiro-selo">
                  {p.logo_url
                    ? <img src={p.logo_url} alt="" loading="lazy" />
                    : <span className="parceiro-iniciais">{getInitials(p.name)}</span>}
                </span>
                <span className="parceiro-nome">{p.name}</span>
              </Tag>
            </li>
          );
        })}
      </ul>
    </section>
  );
};


const getInitials = (nome) =>
  nome
    .split(" ")
    .map((w) => w[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);




/* ──────────────────────────── ABERTURA ────────────────────────────────
   A página não tinha voz nem começo: abria numa barra de 40px e caía
   direto em lista. O masthead diz que estação é essa e, principalmente,
   amarra as seções — o "no ar" conecta acervo e próximo ritual em vez
   de deixar três ilhas soltas.
   ───────────────────────────────────────────────────────────────────── */

const Masthead = ({ rituais, gravacoes }) => {
  const { t } = useTranslation();
  const proximo = rituais?.[0];
  const dias = proximo ? diasAte(proximo.event_date) : null;

  return (
    <header className="masthead">
      <h1 className="masthead-marca">
        {/* Logotipo é preto sobre transparente; o filtro no CSS inverte pra branco */}
        <img
          src={`${process.env.PUBLIC_URL}/assets/mixordia-logo.svg`}
          alt="Mixórdia"
        />
        <span>tv</span>
      </h1>

      <p className="masthead-lead">{t("destaques.tagline")}</p>

      <dl className="masthead-status">
        <div>
          <dt>{t("destaques.acervoLabel")}</dt>
          <dd>{t("destaques.acervoCount", { count: gravacoes.length })}</dd>
        </div>
        {proximo && (
          <div>
            <dt>{t("destaques.proximoRitualLabel")}</dt>
            <dd>
              {proximo.name}
              {dias !== null && <em>{contagem(dias, t)}</em>}
            </dd>
          </div>
        )}
      </dl>
    </header>
  );
};




/* ───────────────────────────── FECHAMENTO ─────────────────────────────
   A página terminava no vácuo: o cara assistia três sets e não tinha
   pra onde ir. O cadastro é a meta nº1 do negócio, então é ele que
   fecha — não um rodapé institucional.
   ───────────────────────────────────────────────────────────────────── */

const Fecho = ({ setShowForm }) => {
  const { t } = useTranslation();
  return (
    <section className="fecho">
      <p className="fecho-linha">{t("destaques.closingLine")}</p>
      <h2>{t("destaques.closingTitle")}</h2>
      <button className="fecho-cta" onClick={() => setShowForm(true)}>
        {t("destaques.closingCta")}
      </button>
    </section>
  );
};




/* ─────────────────────────── PRÓXIMOS RITUAIS ─────────────────────────
   Slot em RETRATO: os flyers deles são verticais (tem até um arquivo
   chamado FLYER_GERAL_PORTRAIT no bucket), e num slot 16:9 o line-up
   ficava cortado. `object-fit: contain` completa a proteção — arte de
   flyer é texto, cortar é perder informação.

   Entra o flyer se existir, senão a cartela de teste. A informação fica
   SEMPRE abaixo do slot, então os dois casos têm o mesmo layout.
   ───────────────────────────────────────────────────────────────────── */

const diasAte = (dateStr) => {
  if (!dateStr) return null;
  const [y, m, d] = dateStr.split("-").map(Number);
  const hoje = new Date();
  hoje.setHours(0, 0, 0, 0);
  return Math.round((new Date(y, m - 1, d) - hoje) / 86400000);
};

/** Não é componente — recebe `t` do chamador em vez de useTranslation(). */
const contagem = (dias, t) => {
  if (dias === null) return null;
  if (dias === 0) return t("destaques.dayToday");
  if (dias === 1) return t("destaques.dayTomorrow");
  if (dias < 30) return t("destaques.inDays", { dias });
  const meses = Math.round(dias / 30);
  return t("destaques.inMonths", { count: meses });
};


/**
 * Ingressos. Sem `external_url` cadastrado o botão não existe — nada de
 * CTA morto prometendo compra que não acontece.
 */
const Ingressos = ({ url, compacto }) => {
  const { t } = useTranslation();
  if (!url) {
    return (
      <span className={`ingressos-vazio ${compacto ? "is-compacto" : ""}`}>
        {t("destaques.ticketsSoon")}
      </span>
    );
  }
  return (
    <a
      className={`ingressos ${compacto ? "is-compacto" : ""}`}
      href={url}
      target="_blank"
      rel="noreferrer"
      onClick={(e) => e.stopPropagation()}
    >
      {t("destaques.ticketsCta")}
    </a>
  );
};


const RituaisBoard = ({ rituais }) => {
  const { t } = useTranslation();
  const faixa = useFaixa(rituais?.length || 0);
  if (!rituais?.length) return null;
  return (
    <section className="board">
      <h2 className="board-title">{t("destaques.rituaisTitle")}</h2>

      {/* Faixa horizontal, não grid: rituais são uma linha do tempo, e
          repetir o grid do merch fazia as duas seções virarem gêmeas. */}
      <div className="faixa-wrap faixa-wrap--ritual">
        {/* Sem espalhar o objeto: `ref` é prop reservada do React e seria
            interceptada, atropelando a ref do container da faixa. */}
        <Setas pode={faixa.pode} mover={faixa.mover} centro={faixa.centro} rotulo={t("destaques.rituaisTitle")} />
        <div className="board-strip" ref={faixa.ref}>
        {rituais.map((r, idx) => {
          const dias = diasAte(r.event_date);
          const [ano, mes, dia] = (r.event_date || "--- -- --").split("-");
          return (
            <article className={`ritual ${idx === 0 ? "is-proximo" : ""}`} key={r.id}>
              <div className="ritual-marca" aria-hidden="true"><span /></div>
              {idx === 0 && <span className="ritual-badge">{t("destaques.badgeProximo")}</span>}
              <div className="ritual-slot">
                {r.image_url ? (
                  <img src={r.image_url} alt="" loading="lazy" />
                ) : (
                  <div className="testcard" aria-hidden="true">
                    <div className="testcard-bars">
                      <i /><i /><i /><i /><i /><i /><i />
                    </div>
                    <div className="testcard-center">
                      <strong>{dia}</strong>
                      <span>{mes}</span>
                    </div>
                  </div>
                )}
              </div>

              <div className="ritual-info">
                <span className="ritual-when">
                  {dia}.{mes}.{ano}
                  {dias !== null && <em>{contagem(dias, t)}</em>}
                </span>
                <h3>{r.name}</h3>
                {r.description && <p>{r.description}</p>}
                <Ingressos url={r.external_url} />
              </div>
            </article>
          );
        })}
        </div>
      </div>
    </section>
  );
};




/* ──────────────────────────────── MERCH ───────────────────────────────
   Encarte de pedido, não vitrine. Sem foto e sem link, uma loja falsa
   ficaria pior que uma lista honesta — e o formato de catálogo mail-order
   abraça o dado que falta em vez de esconder. O preço não entra: o merch
   é vendido na loja do parceiro (external_url); sem link, o card só diz
   "em breve". Quando chegar a foto, as mesmas linhas só ficam mais ricas.
   ───────────────────────────────────────────────────────────────────── */

/** Qual peça é, deduzido do nome — define o desenho técnico. */
const pecaDe = (nome) => {
  const n = nome.toLowerCase();
  if (n.includes("camiseta") || n.includes("camisa")) return "camiseta";
  if (n.includes("tote") || n.includes("bag") || n.includes("bolsa")) return "tote";
  if (n.includes("vinyl") || n.includes("vinil") || n.includes("lp")) return "vinil";
  return "generico";
};

/** Variante de cor citada no nome — vira o preenchimento da silhueta. */
const variante = (nome) => {
  const n = nome.toLowerCase();
  if (n.includes("black") || n.includes("preta")) return "escura";
  if (n.includes("white") || n.includes("branca")) return "clara";
  return "neutra";
};


const Ficha = ({ peca }) => (
  <svg viewBox="0 0 100 100" className="ficha-svg" aria-hidden="true">
    {peca === "camiseta" && (
      <>
        <path
          className="ficha-corpo"
          d="M32 18 L44 12 Q50 19 56 12 L68 18 L82 31 L71 42 L67 36 L67 88 L33 88 L33 36 L29 42 L18 31 Z"
        />
        <rect className="ficha-area" x="40" y="34" width="20" height="26" />
      </>
    )}

    {peca === "tote" && (
      <>
        <path className="ficha-corpo" d="M27 36 H73 V88 H27 Z" />
        <path className="ficha-alca" d="M40 36 V27 a10 9 0 0 1 20 0 V36" />
        <rect className="ficha-area" x="38" y="50" width="24" height="24" />
      </>
    )}

    {peca === "vinil" && (
      <>
        <circle className="ficha-corpo" cx="50" cy="50" r="34" />
        <circle className="ficha-area" cx="50" cy="50" r="13" />
        <circle className="ficha-furo" cx="50" cy="50" r="2.2" />
      </>
    )}

    {peca === "generico" && (
      <>
        <rect className="ficha-corpo" x="24" y="24" width="52" height="52" />
        <rect className="ficha-area" x="36" y="36" width="28" height="28" />
      </>
    )}

    <path className="ficha-marca" d="M8 8 H16 M8 8 V16 M92 92 H84 M92 92 V84" />
  </svg>
);


const MerchEncarte = ({ merch }) => {
  const { t } = useTranslation();
  const faixa = useFaixa(merch?.length || 0);
  if (!merch?.length) return null;
  return (
    <section className="encarte">
      <h2 className="board-title">{t("destaques.merchTitle")}</h2>

      <div className="faixa-wrap faixa-wrap--merch">
        <Setas pode={faixa.pode} mover={faixa.mover} centro={faixa.centro} rotulo={t("destaques.merchTitle")} />
        <div className="merch-grid" ref={faixa.ref}>
        {merch.map((m, i) => {
          const cod = `MXD-${String(i + 1).padStart(3, "0")}`;
          return (
            <article className="merch" key={m.id}>
              <div className={`merch-slot is-${variante(m.name)}`}>
                {m.image_url ? (
                  <img src={m.image_url} alt="" loading="lazy" />
                ) : (
                  <Ficha peca={pecaDe(m.name)} />
                )}
              </div>

              <div className="merch-info">
                <span className="merch-cod">{cod}</span>
                <h3>{m.name}</h3>
                <div className="merch-linha">
                  {m.external_url ? (
                    <a href={m.external_url} target="_blank" rel="noreferrer">{t("destaques.merchOrder")}</a>
                  ) : (
                    <span className="merch-sem">{t("destaques.merchUnavailable")}</span>
                  )}
                </div>
              </div>
            </article>
          );
        })}
        </div>
      </div>
    </section>
  );
};




/* ───────────────────────────── modo GRADE ────────────────────────────── */

const GridMode = ({ rituais, gravacoes, merch, parceiros, openRow, onToggle, setShowForm }) => {
  const { t } = useTranslation();
  return (
  <div className="grade">
    <section className="grade-block">
      <h2>{t("destaques.rituaisTitle")}</h2>

      {rituais.map((item) => (
        <div key={item.id} className="grade-row">
          <div className="grade-head is-estatico">
            <span className="grade-slot">{shortDate(item.event_date) || "—"}</span>
            <span className="grade-title">
              <strong>{item.name}</strong>
              {item.description && <em>{item.description}</em>}
            </span>
            <Ingressos url={item.external_url} compacto />
          </div>
        </div>
      ))}
    </section>

    <section className="grade-block">
      <h2>{t("destaques.gravacoesTitle")}</h2>

      {gravacoes.map((item) => {
        const aberto = openRow?.id === item.id;
        return (
          <div key={item.id} className={`grade-row ${aberto ? "is-open" : ""}`}>
            <button
              className="grade-head"
              onClick={() => onToggle(item)}
              aria-expanded={aberto}
            >
              <span className="grade-slot">{t("destaques.gradeSlotLabel")}</span>
              <span className="grade-title">
                <strong>{item.name}</strong>
                {item.description && <em>{item.description}</em>}
              </span>
              <span className="grade-cue">
                {aberto
                  ? t("destaques.gradeClose")
                  : item.source === "soundcloud"
                    ? t("destaques.gradeListen")
                    : t("destaques.gradeWatch")}
              </span>
            </button>

            {aberto && (
              <div className="grade-player">
                <iframe
                  src={embedUrl(item)}
                  title={item.name}
                  allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            )}
          </div>
        );
      })}
    </section>

    {/* Merch continua em grid mesmo no modo lista: comprar exige ver o
        produto, e uma linha de texto não mostra produto nenhum. */}
    <MerchEncarte merch={merch} />
    <Parceiros parceiros={parceiros} />
    <Fecho setShowForm={setShowForm} />
  </div>
  );
};


export default Destaques;
